import './index.scss';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import _cloneDeep from 'lodash/cloneDeep';
import { Backdrop, Button, Paper, CircularProgress, Typography } from '@material-ui/core';
import { QHeader } from './QHeader';
import { FieldContainer } from '@sharedComponents/Field';
import { QPreview } from './QPreview';
import { makeCurrentQuestionnaire, setQuestionnaire } from '@actions/questionnaireActions';
import {
  makeCreateAnswer,
  makeCurrentAnswer,
  makeUpdateAnswer,
  setCurrentAnswer,
  setEditingAnswer,
  setNavigateToId,
  setViewMode,
} from '@actions/answerActions';
import { setNotification } from '@actions/appActions';
import {
  cleanObject,
  executeLink,
  filterQuestions,
  getUserFullNameInitials,
  translate,
} from '@utils';
import { Paths, QuestionnairePaths } from '@constants/paths';
import {
  currentQuestionnaireFailureSelector,
  currentQuestionnaireLanguageSelector,
  currentQuestionnaireLoadingSelector,
  currentQuestionnaireSelector,
  disableSaveButtonSelector,
  disableSubmitButtonSelector,
  questionnaireHeaderLabelsSelector,
} from '@selectors/questionnaire';
import _isEqual from 'lodash/isEqual';
import _isEmpty from 'lodash/isEmpty';
import {
  currentUserSelector,
  lastAnswerIdSelector,
  lastQuestionnaireIdSelector,
} from '@selectors/user';
import {
  answerEditModeSelector,
  currentAnswerLoadingSelector,
  currentAnswerSelector,
  navigateAnswerIdSelector,
  showAgreementLinkSelector,
} from '@selectors/answer';
import {
  setAnswerContext,
  setAnswerEnvLanguage,
  setAnswerQuestionnaireContext,
  setAnswerUser,
  setAnswerUserAbbr,
} from '@actions/answerEnvironmentActions';
import { Agreement } from '@sharedComponents/Agreement';
import { isUserSelector } from '@selectors/user/rolesSelector';
import { failure } from '@actions/commonActions';
import { CompaniesEnum, ConfigKeys } from '@enums';
import cx from 'classnames';
import { useBeforeUnload } from '@hooks/useBeforeUnload';

export const Questionnaire = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const defaultFormState = { experience: 'IT&nIT' };

  const { id } = useParams();
  const [form, setForm] = useState(defaultFormState);
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [agreed, setAgreed] = useState<boolean>(false);

  const currentUser = useSelector(currentUserSelector);
  const isUserRole = useSelector(isUserSelector);
  const isCurrentQuestionnaireFailure = useSelector(currentQuestionnaireFailureSelector);
  const isCurrentQuestionnaireLoading = useSelector(currentQuestionnaireLoadingSelector);
  const currentQuestionnaire = useSelector(currentQuestionnaireSelector);
  const currentHeaderLabels = useSelector(questionnaireHeaderLabelsSelector);
  const lastQuestionnaireId = useSelector(lastQuestionnaireIdSelector);
  const isSubmittingDisabled = useSelector(disableSubmitButtonSelector(agreed));
  const isSavingDisabled = useSelector(disableSaveButtonSelector(agreed));

  const editMode = useSelector(answerEditModeSelector);
  const currentAnswer = useSelector(currentAnswerSelector);
  const isCurrentAnswerLoading = useSelector(currentAnswerLoadingSelector);
  const navigateAnswerId = useSelector(navigateAnswerIdSelector);
  const lastAnswerId = useSelector(lastAnswerIdSelector);
  const agreementLink = executeLink(ConfigKeys.AGREEMENT_LINK);
  const showAgreementLink = useSelector(showAgreementLinkSelector);

  const lang = useSelector(currentQuestionnaireLanguageSelector);

  useBeforeUnload(isFormDirty, translate('leave_page_warning'));

  useEffect(() => {
    if (lang) {
      dispatch(setAnswerEnvLanguage(lang));
    }
  }, [lang]);

  useEffect(() => {
    dispatch(makeCurrentQuestionnaire(Number(id)));
    return () => {
      dispatch(setViewMode(false));
      dispatch(setEditingAnswer(false));
      dispatch(setQuestionnaire(null));
      dispatch(setCurrentAnswer(null));
    };
  }, [id]);

  useEffect(() => {
    if (Number(id) === lastQuestionnaireId && isUserRole) {
      dispatch(makeCurrentAnswer(lastAnswerId));
      dispatch(setViewMode(true));
    }

    return () => {
      dispatch(setCurrentAnswer(null));
    };
  }, [id, lastAnswerId, lastQuestionnaireId, isUserRole]);

  useEffect(() => {
    if (navigateAnswerId) {
      navigate(`/answer/${navigateAnswerId}`);
      dispatch(setNavigateToId(null));
    }
  }, [navigateAnswerId]);

  useEffect(() => {
    if (isCurrentQuestionnaireFailure) {
      navigate(`${Paths.QUESTIONNAIRE}/${QuestionnairePaths.NOT_FOUND}`);
    }
    return () => {
      dispatch(failure(false, 'current_questionnaire'));
    };
  }, [isCurrentQuestionnaireFailure]);

  useEffect(() => {
    if (currentAnswer) {
      const currentAnswerData = JSON.parse(currentAnswer.data);
      setForm(currentAnswerData);
      setAgreed(Boolean(currentAnswer.report));
      dispatch(setAnswerContext(currentAnswer.data));
    }
  }, [currentAnswer]);

  useEffect(() => {
    if (!editMode && currentQuestionnaire) {
      dispatch(setAnswerUser(currentUser));
      dispatch(setAnswerUserAbbr(getUserFullNameInitials(currentUser)));
      dispatch(setAnswerQuestionnaireContext(currentQuestionnaire.data));
      dispatch(setAnswerContext(JSON.stringify(defaultFormState)));
    }
  }, [currentQuestionnaire, editMode]);

  const handleQuestionChange = useCallback(
    (key) => (value) => {
      const result = _cloneDeep(form);
      result[key] = value;

      if (currentAnswer) {
        _isEqual(result, currentAnswer.data) ? setIsFormDirty(false) : setIsFormDirty(true);
      } else {
        if (Object.values(result).every((x) => x === null || x === '' || _isEmpty(x))) {
          setIsFormDirty(false);
        } else {
          setIsFormDirty(true);
        }
      }
      setForm(result);
      dispatch(setAnswerContext(JSON.stringify(result)));
    },
    [form, setForm]
  );

  const sendAnswer = () => {
    setIsFormDirty(false);
    dispatch(
      makeCreateAnswer({
        questionnaireName: currentQuestionnaire.name,
        data: cleanObject(form),
        agreed,
      })
    );
  };

  const changeAnswer = () => {
    setIsFormDirty(false);
    dispatch(
      makeUpdateAnswer(currentAnswer.id, {
        data: cleanObject(form),
      })
    );
    dispatch(setNotification({ textKey: 'answer_changed', variant: 'success' }));
  };

  const SaveButton = (
    <Button onClick={changeAnswer} size="large" disabled={isSavingDisabled} data-testid="save">
      {translate('save')}
    </Button>
  );
  const SendButton = (
    <Button onClick={sendAnswer} size="large" disabled={isSubmittingDisabled} data-testid="send">
      {translate('send')}
    </Button>
  );
  if (isCurrentQuestionnaireLoading || !currentQuestionnaire || isCurrentAnswerLoading) {
    return (
      <Backdrop className="backdrop-container" open={isCurrentQuestionnaireLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  const questions = JSON.parse(currentQuestionnaire.data).questions;

  return (
    <div className="questionnaire-page__wrapper">
      <div className="questionnaire-page__buttons-top"></div>
      <QHeader labels={currentHeaderLabels} />
      <div className="questionnaire-page__container">
        <div
          className={cx('questionnaire-page__questions', {
            'questionnaire-page__questions-vention':
              currentQuestionnaire.company === CompaniesEnum.Vention,
          })}
        >
          <Typography variant="h6" component="h6" className="questionnaire-page__preview-heading">
            {translate('questionnaire_title')}
          </Typography>
          <Paper className="paper">
            {filterQuestions(form, questions, lang).map((item) => (
              <FieldContainer
                key={item.key}
                user={currentAnswer?.owner || currentUser}
                form={form}
                handleChange={handleQuestionChange(item.key)}
                schema={item}
              />
            ))}
          </Paper>
          {showAgreementLink && (
            <Agreement onChange={() => setAgreed(!agreed)} checked={agreed} link={agreementLink} />
          )}
        </div>
        <div data-testid="questionnaire-preview" className="questionnaire-page__preview">
          <Typography variant="h6" component="h6" className="questionnaire-page__preview-heading">
            {translate('preview_title')}
          </Typography>
          <QPreview />
        </div>
      </div>
      <div className="questionnaire-page__buttons-bottom">{editMode ? SaveButton : SendButton}</div>
    </div>
  );
};
